<template>
  <!-- Data View -->
  <div
    class="tw:flex tw:flex-col tw:rounded-xl tw:min-h-full tw:border tw:border-gray-300 tw:h-full"
    :class="{ 'tw:grayscale': noFilterData }"
    v-show="currentView === 'data'"
  >
    <!-- Header Section -->
    <div class="tw:bg-gray-100 tw:p-4 tw:rounded-t-xl tw:flex-shrink-0 tw:border-b tw:border-gray-300">
      <div class="tw:mb-5 tw:flex tw:justify-between tw:items-start tw:gap-x-2">
        <div class="tw:flex tw:flex-row tw:items-center tw:gap-x-2 tw:flex-1 tw:min-w-0">
          <div class="tw:rounded-full tw:p-3 tw:bg-decorative-lightblue tw:flex-shrink-0">
            <img :src="clicks" :alt="title" class="tw:h-6 tw:w-6 tw:object-contain" />
          </div>
          <div class="tw:flex-1 tw:min-w-0">
            <div class="tw:flex tw:items-center tw:gap-x-1">
              <p class="tw:font-bold tw:text-lg">{{ $t(title) }}</p>
              <UiTooltip tooltip-position="top" :label="$t(hint)" class="tw:cursor-pointer tw:flex-shrink-0">
                <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[16px] tw:w-[16px]" />
              </UiTooltip>
            </div>

            <i18n-t
              keypath="rc_performance_clicks_benchmark"
              tag="p"
              v-if="progression >= 0 || noFilterData"
              class="tw:text-sm"
            >
              <template #Percent>
                <span class="tw:font-bold">↑ {{ noFilterData ? '-' : formatPercentage(progression, 0, 2) }}</span>
              </template>
            </i18n-t>

            <i18n-t
              keypath="rc_performance_clicks_benchmark"
              tag="p"
              v-if="progression < 0 && !noFilterData"
              class="tw:text-sm"
            >
              <template #Percent>
                <span class="tw:font-bold">↓ {{ formatPercentage(progression, 0, 2) }}</span>
              </template>
            </i18n-t>
          </div>
        </div>

        <!-- Graph button -->
        <div class="tw:flex-shrink-0">
          <button
            v-if="hasPartnerData"
            class="tw:hover:cursor-pointer tw:rounded tw:hover:bg-gray-200 tw:h-[36px] tw:w-[36px] tw:flex tw:items-center tw:justify-center"
            @click="switchView"
          >
            <GraphIcon class="tw:size-6" />
          </button>
        </div>
      </div>

      <div class="tw:flex tw:items-center tw:justify-between" v-if="!partnerIsFetching && !isLoading">
        <p class="tw:text-sm tw:font-bold">
          {{ $t('rc_performance_total_clicks') }}
        </p>

        <p class="tw:font-bold tw:text-[32px]">
          {{ total > 0 ? formatNumber(total, { maximumFractionDigits: 2, minimumFractionDigits: 0 }) : '-' }}
        </p>
      </div>
    </div>

    <!-- Single Column Layout - All Items Stacked -->
    <div class="tw:bg-white tw:rounded-b-xl tw:flex tw:flex-col tw:flex-1">
      <!-- Partners -->
      <div
        v-for="(partner, index) in partnerDistributionWithNames"
        :key="index"
        class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-gray-300 tw:px-4 tw:py-2 tw:min-h-[10px] tw:gap-x-2"
      >
        <div class="tw:text-sm tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0">
          <span class="tw:h-2 tw:w-2 tw:block tw:rounded-full tw:flex-shrink-0" :class="partner.color" />
          <span>
            {{ partner.name !== 'rc_performance_other_sites' ? partner.name : $t('rc_performance_other_sites') }}
          </span>
        </div>
        <p class="tw:text-2xl tw:flex-shrink-0 tw:whitespace-nowrap">{{ partner.value }}</p>
      </div>

      <!-- Third Partner -->
      <div
        v-if="!hasRateConnect && thirdPartner"
        class="tw:flex tw:items-center tw:justify-between tw:border-b tw:border-gray-300 tw:py-2 tw:px-4 tw:min-h-[10px] tw:gap-x-2"
      >
        <div class="tw:text-sm tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0">
          <span class="tw:h-2 tw:w-2 tw:block tw:rounded-full tw:flex-shrink-0" :class="thirdPartner.color" />
          <span>{{ $t(thirdPartner.name) }}</span>
        </div>
        <p class="tw:text-2xl tw:flex-shrink-0 tw:whitespace-nowrap">{{ thirdPartner.value }}</p>
      </div>

      <!-- Website Rates - Last item, no bottom border -->
      <div class="tw:px-4 tw:py-2 tw:flex tw:min-h-[10px] tw:flex-1 tw:items-center">
        <!-- Has RateConnect -->
        <router-link
          v-if="!noFilterData && hasRateConnect"
          :to="{ name: 'rate-connect-running-performance' }"
          @click="triggerEvent"
          data-testid="direct-rate-redirect-link"
          class="tw:group tw:flex tw:w-full tw:items-center tw:justify-between tw:hover:cursor-pointer tw:no-underline tw:text-inherit tw:transition-colors tw:gap-x-2"
        >
          <div class="tw:text-sm tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0">
            <span class="tw:bg-blue-700 tw:h-2 tw:w-2 tw:block tw:rounded-full tw:flex-shrink-0" />
            <span>{{ $t('rc_performance_website_rates_clicks') }}</span>
          </div>
          <div class="tw:flex tw:gap-x-1 tw:items-center tw:flex-shrink-0">
            <p class="tw:text-2xl">{{ formattedValue }}</p>
            <div class="tw:rounded tw:p-1 tw:transition-colors tw:group-hover:bg-gray-100">
              <ChevronRight class="tw:size-6" />
            </div>
          </div>
        </router-link>

        <!-- No RateConnect -->
        <div
          v-else
          class="tw:group tw:flex tw:w-full tw:items-center tw:justify-between tw:text-inherit tw:transition-colors tw:gap-x-2"
          :class="!hasRateConnect && !noFilterData ? 'tw:hover:cursor-pointer' : ''"
          @click="!hasRateConnect && !noFilterData ? openPopupFn() : null"
          role="button"
          tabindex="0"
          :aria-disabled="hasRateConnect || noFilterData"
          @keydown.enter.prevent="!hasRateConnect && !noFilterData ? openPopupFn() : null"
          @keydown.space.prevent="!hasRateConnect && !noFilterData ? openPopupFn() : null"
        >
          <div class="tw:text-sm tw:flex tw:items-center tw:gap-2 tw:flex-1 tw:min-w-0">
            <span
              class="tw:h-2 tw:w-2 tw:block tw:rounded-full tw:flex-shrink-0"
              :class="!hasRateConnect && !noFilterData ? 'tw:bg-gray-500' : 'tw:bg-blue-700'"
            />
            <span>{{ $t('rc_performance_website_rates_clicks') }}</span>
          </div>

          <div class="tw:flex tw:gap-x-1 tw:items-center tw:flex-shrink-0">
            <!-- Locker icon -->
            <div
              v-if="!hasRateConnect && !noFilterData"
              class="tw:rounded tw:p-1 tw:transition-colors tw:group-hover:bg-gray-200 tw:flex tw:items-center tw:justify-center tw:h-[28px] tw:w-[28px] tw:self-center tw:flex-shrink-0"
            >
              <Locker class="tw:block tw:group-hover:hidden tw:w-[14px] tw:h-[18px]" />
              <LockerOpen class="tw:hidden tw:group-hover:block tw:w-[14px] tw:h-[18px]" />
            </div>

            <!-- Regular value -->
            <p v-else class="tw:text-2xl">{{ formattedValue }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Graph View -->
  <div
    class="tw:flex tw:flex-col tw:rounded-xl tw:min-h-full tw:border tw:border-gray-300 tw:p-4"
    v-show="currentView === 'graph'"
  >
    <div class="tw:flex tw:items-center tw:justify-between">
      <h3 class="tw:font-bold tw:text-xl">{{ $t(title) }}</h3>
      <button class="tw:hover:cursor-pointer" @click="switchView" data-testid="switchGraphBtn">
        <Close class="tw:fill-black tw:size-3.5" />
      </button>
    </div>

    <ClicksGraph
      containerId="clicks-graph"
      :colors="['#FFCC31', '#FF932C', '#0079C2']"
      :series="chartDataSeries"
      :total="total"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import GraphIcon from '@/components/icons/GraphIcon.svg?component'
import ClicksGraph from '@/components/homePage/performance/ClicksGraph.vue'
import Close from '@/components/icons/Close.svg?component'
import Locker from '@/components/icons/Locker.svg?component'
import LockerOpen from '@/components/icons/LockerOpen.svg?component'

import appAnalytics from '@/utils/tracking'
import { usePartnerNames } from '../queries/conversionFunnel'
import { selectedAccommodationId } from '@/layouts/queries'
import { useEligibilityPopup } from '@/components/homePage/performance/useEligibilityPopup'
import { useFormatting } from '@/composables/useFormatting'

const { t } = useI18n()

const props = defineProps({
  id: String,
  title: String,
  hint: String,
  value: Number,
  progression: Number,
  total: Number,
  noFilterData: Boolean,
  partnerDistribution: Object,
  hasRateConnect: Boolean,
  isRateConnectEligible: Boolean,
})

const currentView = ref('data')
const { openLockedMetricPopup } = useEligibilityPopup()
const { formatNumber, formatCurrency, formatPercentage } = useFormatting()

const clicks = computed(() => new URL(`../../../assets/img/homepage/ClicksIcon.png`, import.meta.url))

// Check if there's actual partner data
const hasPartnerData = computed(() => {
  if (!props.partnerDistribution) return false
  const keys = Object.keys(props.partnerDistribution)
  if (keys.length === 0) return false
  return Object.values(props.partnerDistribution).some(value => value > 0)
})

const switchView = () => {
  currentView.value = currentView.value === 'data' ? 'graph' : 'data'
  appAnalytics.track(appAnalytics.events.HOMEPAGE_CARDS_SHOW_CHART, {
    itemId: selectedAccommodationId.value,
    cardType: 'clicks',
  })
}

const partnerIds = computed(() => {
  if (!props.partnerDistribution) return []
  const ids = Object.keys(props.partnerDistribution)
  return ids.map(Number)
})

const { isLoading, partnerIsFetching, partners } = usePartnerNames(partnerIds)

const partnerDistributionWithNames = computed(() => {
  if (!props.partnerDistribution) return []

  const partnersArray = Object.entries(props.partnerDistribution).map(([id, value]) => ({ id, value }))
  const sorted = partnersArray.sort((a, b) => b.value - a.value)

  let result
  if (props.hasRateConnect) {
    const topValues = sorted.slice(0, 1)
    const restSum = sorted.slice(1).reduce((sum, item) => sum + item.value, 0)

    // Only add "other" if there's actual data
    if (restSum > 0) {
      result = [...topValues, { id: 'other_values', value: restSum }]
    } else if (topValues.length > 0 && topValues[0].value > 0) {
      result = topValues
    } else {
      return []
    }
  } else {
    result = sorted.slice(0, 2)

    // If no data, return empty array
    if (result.length === 0 || result.every(item => item.value === 0)) {
      return []
    }
  }

  return result.map(item => {
    let name
    if (item.id === 'other_values') {
      name = 'rc_performance_other_sites'
    } else {
      const partner = partners.value?.find(p => p.nsid.id == item.id)
      name = partner?.originalName?.value || partner?.name || `Partner ${item.id}`
    }

    return {
      ...item,
      name: name,
      color: name !== 'rc_performance_other_sites' ? 'tw:bg-decorative-yellow' : 'tw:bg-decorative-orange',
    }
  })
})

const thirdPartner = computed(() => {
  if (props.hasRateConnect || !props.partnerDistribution) return null

  const partnersArray = Object.entries(props.partnerDistribution).map(([id, value]) => ({ id, value }))
  const sorted = partnersArray.sort((a, b) => b.value - a.value)
  const restSum = sorted.slice(2).reduce((sum, item) => sum + item.value, 0)

  // Return null if no data
  if (restSum === 0) return null

  return {
    id: 'other_values',
    value: restSum,
    name: 'rc_performance_other_sites',
    color: 'tw:bg-decorative-orange',
  }
})

const formattedValue = computed(() => {
  if (props.value == null || !Number.isFinite(props.value)) return '-'
  if (props.id === 'revenue') {
    return formatCurrency(props.value)
  } else {
    return formatNumber(props.value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }
})
const transformPartnerDistribution = partnerDistribution => {
  const partnersArray = Object.entries(partnerDistribution)
    .map(([id, value]) => ({ id, value }))
    .filter(item => item.value > 0)

  const sorted = partnersArray.sort((a, b) => b.value - a.value)
  const top2 = sorted.slice(0, 2)
  const restSum = sorted.slice(2).reduce((sum, item) => sum + item.value, 0)
  const total = sorted.reduce((sum, item) => sum + item.value, 0)
  const result = [...top2]

  if (sorted.length > 2 && restSum > 0) {
    result.push({
      id: 'other_values',
      value: restSum,
    })
  }

  return { data: result, total }
}

const chartDataSeries = computed(() => {
  if (!props.partnerDistribution) return []

  const { data, total } = transformPartnerDistribution(props.partnerDistribution)

  return data.map(item => {
    const percentage = total > 0 ? item.value / total : 0
    const formattedPercentage = formatPercentage(percentage * 100, 0, 1)

    let name
    if (item.id === 'other_values') {
      name = t('rc_performance_website_rates_clicks')
    } else {
      const partner = partners.value?.find(p => p.nsid.id == item.id)
      name = partner?.originalName?.value || partner?.name || `Partner ${item.id}`
    }

    return {
      name: `${formattedPercentage} - ${name}`,
      y: percentage * 100,
    }
  })
})

const triggerEvent = () => {
  appAnalytics.track(appAnalytics.events.HOMEPAGE_RATE_CONNECT_CLICKS_CLICKED, {
    accommodationId: selectedAccommodationId.value,
  })
}

const openPopupFn = () => {
  openLockedMetricPopup(props.isRateConnectEligible)
  appAnalytics.track(appAnalytics.events.HOMEPAGE_LOCK_ICON_CLICKED, {
    itemId: selectedAccommodationId.value,
    cardType: 'clicks',
  })
}
</script>
